(() => {
  // Use localStorage for persistent caching with memory fallback
  const CACHE_KEY = 'country_matcher_cache';
  const CACHE_TIMESTAMP_KEY = 'country_matcher_timestamp';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  
  // Memory cache for faster lookups
  const memoryCache = new Map();
  const imageCache = new Map();

  // Enhanced country aliases using Map for O(1) lookup
  const countryAliasMap = new Map([
    ['united states', new Set(['usa', 'united states', 'united states of america', 'us', 'united states sim card', 'usa sim card'])],
    ['uae', new Set(['uae', 'united arab emirates', 'uae (dubai)', 'dubai', 'emirates'])],
    ['uk', new Set(['uk', 'united kingdom', 'great britain', 'britain', 'england'])],
    ['palestine', new Set(['palestine', 'state of palestine', 'palestinian territories'])],
    ['türkiye', new Set(['türkiye', 'turkey', 'turkiye'])],
    ['macedonia', new Set(['macedonia', 'north macedonia'])],
    ['taiwan', new Set(['taiwan', 'republic of china', 'chinese taipei'])],
    ['south korea', new Set(['south korea', 'korea', 'republic of korea'])],
    ['russia', new Set(['russia', 'russian federation'])],
    ['vietnam', new Set(['vietnam', 'viet nam'])],
    ['china', new Set(['china', 'mainland china', 'peoples republic of china'])],
    ['hong kong', new Set(['hong kong', 'hong kong sar', 'hong kong china'])],
    ['macau', new Set(['macau', 'macao', 'macao sar'])],
    ['czech republic', new Set(['czech republic', 'czechia'])],
    ['vatican city', new Set(['vatican city', 'holy see', 'vatican'])],
    ['saudi arabia', new Set(['saudi arabia', 'ksa'])],
    ['brunei', new Set(['brunei', 'brunei darussalam'])],
    ['laos', new Set(['laos', 'lao', 'lao pdr'])],
    ['myanmar', new Set(['myanmar', 'burma'])],
    ['timor-leste', new Set(['timor-leste', 'east timor', 'timor'])],
  ]);

  // Common text patterns to clean from collection titles
  const cleanPatterns = [
    / sim card/gi,
    / esim/gi,
    /-sim-card/gi,
    / prepaid/gi,
    / postpaid/gi,
    / tourist/gi,
    / data/gi,
    / plan/gi,
    / package/gi,
    / mobile/gi,
    / roaming/gi
  ];

  class CountryMatcher {
    constructor(config) {
      this.assetBaseUrl = config.assetBaseUrl;
      this.countriesUrl = config.countriesUrl;
      this.countryData = null;
      this.init();
      this.errorCount = new Map(); // Track error counts per country
    }

    init() {
      // Try memory cache first, then localStorage
      this.countryData = memoryCache.get(CACHE_KEY);
      if (this.countryData) {
        this.initElements();
        return;
      }

      this.countryData = this.getCachedData();
      if (this.countryData) {
        memoryCache.set(CACHE_KEY, this.countryData);
        this.initElements();
        // Preload flags after initialization
        requestIdleCallback(() => this.preloadCommonFlags());
        return;
      }

      // Fetch fresh data if cache miss
      this.fetchCountryData();
    }

    getCachedData() {
      try {
        const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (timestamp && Date.now() - parseInt(timestamp) < CACHE_DURATION) {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) return JSON.parse(cached);
        }
      } catch (e) {
        console.warn('Cache access failed:', e);
      }
      return null;
    }

    setCachedData(data) {
      try {
        memoryCache.set(CACHE_KEY, data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (e) {
        console.warn('Cache write failed:', e);
      }
    }

    preloadImage(src) {
      if (imageCache.has(src)) {
        return Promise.resolve(imageCache.get(src));
      }

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          imageCache.set(src, img);
          resolve(img);
        };
        img.onerror = reject;
        img.src = src;
      });
    }

    // Preload common flags during idle time
    preloadCommonFlags() {
      const commonCountries = ['us', 'uk', 'au', 'ca', 'nz'];
      let index = 0;

      const preloadNext = () => {
        if (index >= commonCountries.length) return;
        
        const code = commonCountries[index++];
        const flagSrc = `${this.assetBaseUrl}${code}.svg`;
        
        this.preloadImage(flagSrc)
          .catch(() => {}) // Silently fail
          .finally(() => {
            requestIdleCallback(preloadNext);
          });
      };

      requestIdleCallback(preloadNext);
    }

    cleanCollectionTitle(title) {
      let cleaned = title.toLowerCase();
      cleanPatterns.forEach(pattern => {
        cleaned = cleaned.replace(pattern, '');
      });
      return cleaned.trim();
    }

    findCountryMatch(collectionTitle, data) {
      const cleanedTitle = this.cleanCollectionTitle(collectionTitle);
      
      // Direct match first
      const directMatch = data.find(country => 
        country.name.toLowerCase() === cleanedTitle
      );
      if (directMatch) return directMatch;

      // Check aliases using Map
      for (const [countryName, aliases] of countryAliasMap) {
        if (aliases.has(cleanedTitle)) {
          const match = data.find(country => 
            country.name.toLowerCase() === countryName
          );
          if (match) return match;
        }
      }

      // Fuzzy match as last resort
      return data.find(country => {
        const countryName = country.name.toLowerCase();
        return cleanedTitle.includes(countryName) || countryName.includes(cleanedTitle);
      });
    }

    async updateFlag(element, flagSrc) {
      const img = element.querySelector('img');
      if (!img || img.src === flagSrc) return;

      try {
        await this.preloadImage(flagSrc);
        requestAnimationFrame(() => {
          img.src = flagSrc;
          img.style.opacity = '1';
        });
      } catch (error) {
        console.error('Error loading flag:', error);
        this.handleError(element, flagSrc);
      }
    }

    handleError(element, flagSrc) {
      const img = element.querySelector('img');
      const placeholderFlag = element.dataset.placeholderFlag;
      
      // Track error count for this flag
      const errorCount = (this.errorCount.get(flagSrc) || 0) + 1;
      this.errorCount.set(flagSrc, errorCount);

      // Log error details for debugging
      if (errorCount === 1) {
        console.warn(`Failed to load flag: ${flagSrc}`);
        console.warn('Collection title:', element.dataset.collectionTitle);
      }

      if (img && img.src !== placeholderFlag) {
        requestAnimationFrame(() => {
          img.src = placeholderFlag;
          img.style.opacity = '1';
        });
      }
    }

    async processElement(element) {
      if (!this.countryData) return;

      const collectionTitle = element.dataset.collectionTitle;
      const mobileCustomImage = element.dataset.mobileCustomImage;
      const customImage = element.dataset.customImage;
      const collectionImage = element.dataset.collectionImage;
      const placeholderFlag = element.dataset.placeholderFlag;

      try {
        // Try custom images first
        const customFlagSrc = customImage || mobileCustomImage || collectionImage;
        if (customFlagSrc) {
          const img = element.querySelector('img');
          if (customImage) {
            img.classList.add('custom-flag');
          }
          await this.updateFlag(element, customFlagSrc);
          return;
        }

        // If no custom image, try country matching
        const countryMatch = this.findCountryMatch(collectionTitle, this.countryData);
        if (countryMatch) {
          const code = countryMatch.code.toLowerCase();
          const flagSrc = `${this.assetBaseUrl}${code}.svg`;
          const img = element.querySelector('img');
          img.classList.remove('custom-flag');
          await this.updateFlag(element, flagSrc);
        } else {
          // Log unmatched country for debugging
          console.warn(`No country match found for: ${collectionTitle}`);
          this.handleError(element, placeholderFlag);
        }
      } catch (error) {
        console.error('Error processing country match:', error);
        this.handleError(element, placeholderFlag);
      }
    }

    initElements() {
      // Use a single IntersectionObserver instance
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.processElement(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '200px', // Load earlier
        threshold: 0
      });

      // Batch DOM operations
      const elements = document.querySelectorAll('[data-country-matcher]');
      requestAnimationFrame(() => {
        elements.forEach(element => observer.observe(element));
      });
    }

    async fetchCountryData() {
      try {
        const response = await fetch(this.countriesUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        
        this.countryData = await response.json();
        this.setCachedData(this.countryData);
        this.initElements();
        
        // Preload flags during idle time
        requestIdleCallback(() => this.preloadCommonFlags());
      } catch (error) {
        console.error('Error loading countries:', error);
        // Handle the error by showing placeholder flags
        document.querySelectorAll('[data-country-matcher]').forEach(el => 
          this.handleError(el, el.dataset.placeholderFlag)
        );
      }
    }
  }

  // Initialize when DOM is ready
  const initCountryMatcher = () => {
    const config = window.countryMatcherConfig || {};
    if (!config.assetBaseUrl || !config.countriesUrl) {
      console.error('Country matcher configuration is missing required parameters');
      return;
    }
    new CountryMatcher(config);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCountryMatcher);
  } else {
    initCountryMatcher();
  }
})(); 